import React, {
  useEffect, useCallback, useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { SidebarSelectors, MeSelectors, ModalsSelectors } from '../../../redux/selectors';
import { AsyncActionStatus } from '../../../redux/enums';
import {
  SidebarActionCreators, MeActionCreators, DraftToolbarActionCreators, ModalsActionCreators, PackagesActionCreators,
} from '../../../redux/actions/creators';
import { userRoleCheckers } from '../../../../../shared/helpers';
import { useResponsive } from '../../../hooks';
import STLogo from '../../../../assets/logo.svg';
import CrossRed from '../../../../assets/cross-red.svg';
import Hamburger from '../../../../assets/hamburger.svg';

import OnboardingModal from '../OnboardingModal';
import FeatureInfoModal from '../FeatureInfoModal';
import getWriterOnboardingContent from '../OnboardingModal/getWriterOnboardingContent';
import getCustomerOnboardingContent from '../OnboardingModal/getCustomerOnboardingContent';
import SidebarMenuItems from './SidebarMenuItems';
import { ROUTES } from '../../../router';
import { RolesGuard, To } from '../../../../rolesGuard/rolesGuard';
import { UserInteractions } from '../../../../../shared/enums';
import ExtensionFeePaymentModal from '../ExtensionFeePaymentModal';
import AccessToProjectBlocked from '../AccessToProjectBlocked';
import ActiveSubscriptionWarn from '../ActiveSubscriptionWarn';

import styles from '../../../styles/views/components/SidebarMenu/SidebarMenu.module.scss';

const SidebarMenu = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isExpanded = useSelector(SidebarSelectors.selectIsSidebarExpanded);
  const me = useSelector(MeSelectors.selectMe);
  const isOnboardingModalOpen = useSelector(ModalsSelectors.selectOnboardingModalIsOpen);
  const isActiveSubscriptionWarnModalShown = useSelector(ModalsSelectors.selectActiveSubscriptionWarnModalIsOpen);
  const { isMobile, isTablet, isDesktop } = useResponsive();
  const [t] = useTranslation();

  const isCustomerHasActiveSubscription = useMemo(
    () => userRoleCheckers.isCustomer(me?.role) && me.project.hasActiveSubscription,
    [me?.role, me?.project?.hasActiveSubscription],
  );
  const isOnboardingAvailable = userRoleCheckers.isWriter(me.role)
    || userRoleCheckers.isEditor(me.role)
    || userRoleCheckers.isCustomer(me.role);

  const isDraftOrVersionRoute = useMemo(() => {
    const isVersionsRoute = matchPath({ path: ROUTES.projects.versions.byProjectId.path }, location.pathname)
      || matchPath({ path: ROUTES.projects.versions.general.path }, location.pathname);
    const isDraftRoute = matchPath({ path: ROUTES.projects.draft.byProjectId.path }, location.pathname)
      || matchPath({ path: ROUTES.projects.draft.byChapterIndex.path }, location.pathname);
    return isDraftRoute || isVersionsRoute;
  }, [location.pathname]);

  const expandSidebar = useCallback(() => {
    dispatch(SidebarActionCreators.expandSidebar());
    if (isDraftOrVersionRoute) dispatch(DraftToolbarActionCreators.normalView());
  }, [dispatch, isDraftOrVersionRoute]);

  useEffect(() => {
    if (isDesktop) expandSidebar();
  }, [isDesktop, dispatch, expandSidebar]);

  useEffect(() => {
    if (!me.interactions?.isChatbotSeen && RolesGuard.letMe(To.SeeChatBotInfoModal, me.role)) {
      dispatch(ModalsActionCreators.openChatBotInfoModal());
    }
    if (!me.interactions?.isOnboardingPassed && isOnboardingAvailable) {
      dispatch(ModalsActionCreators.openOnboardingModal());
    }
    if (me.project?.isProjectAccessBlocked) {
      dispatch(ModalsActionCreators.openExtensionFeePaymentModal());
    }
  }, [me, isOnboardingAvailable, dispatch]);

  const handleCloseOnboardingModal = useCallback(() => {
    if (me.interactions?.isOnboardingPassed) {
      dispatch(ModalsActionCreators.closeOnboardingModal());
    } else {
      dispatch(MeActionCreators.updateUserInteractions[AsyncActionStatus.PENDING](me._id, UserInteractions.isOnboardingPassed));
    }
  }, [dispatch, me._id, me.interactions?.isOnboardingPassed]);

  const requestExtensionFeePayment = useCallback(() => {
    dispatch(ModalsActionCreators.closeActiveSubscriptionWarnModal());
    dispatch(PackagesActionCreators.requestExtensionFeePayment[AsyncActionStatus.PENDING]());
  }, [dispatch]);

  const onboardingContent = userRoleCheckers.isWriter(me.role) ? getWriterOnboardingContent() : getCustomerOnboardingContent();

  return (
    <div className={`${styles.menuWrapper} ${isExpanded ? styles.menuWrapperExpanded : styles.menuWrapperCollapsed}`}>
      <div className={`${styles.menu} ${isExpanded ? '' : styles.menuCollapsed}`}>
        <div className={styles.header}>
          {isDesktop && !isExpanded && (
            <div
              className={styles.hamburgerIcon}
              onClick={expandSidebar}
            >
              <Hamburger />
            </div>
          )}
          <div
            className={styles.closeIcon}
            onClick={() => dispatch(SidebarActionCreators.collapseSidebar())}
          >
            {(isMobile || isTablet || (isDesktop && isExpanded)) && <CrossRed />}
          </div>
          <div className={styles.logoIcon}>
            {isMobile && <STLogo width="108" height="24" />}
            {isTablet && <STLogo width="160" height="32" />}
            {isDesktop && isExpanded && <STLogo width="160" height="32" />}
          </div>
        </div>
        {isExpanded && (
          <div className={styles.menuHolder}>
            <SidebarMenuItems me={me} />
            {isOnboardingAvailable && (
              <div onClick={() => dispatch(ModalsActionCreators.openOnboardingModal())} className={styles.howItWorksWrapper}>
                <i className={styles.howItWorksIcon} />
                {t('onboarding.howItWorks', { ns: i18next.Namespace.COMMON })}
              </div>
            )}
          </div>
        )}

        <OnboardingModal
          isShown={isOnboardingModalOpen}
          closeModal={handleCloseOnboardingModal}
          content={onboardingContent}
        />

        <FeatureInfoModal.ChatBot />

        <AccessToProjectBlocked />

        <ExtensionFeePaymentModal
          primaryAction={isCustomerHasActiveSubscription
            ? () => dispatch(ModalsActionCreators.openActiveSubscriptionWarnModal())
            : requestExtensionFeePayment}
        />

        <ActiveSubscriptionWarn
          isShown={isActiveSubscriptionWarnModalShown}
          primaryAction={requestExtensionFeePayment}
          closeModal={() => dispatch(ModalsActionCreators.closeActiveSubscriptionWarnModal())}
        />
      </div>
    </div>
  );
};

export default SidebarMenu;
